/**
 * getBoxInfo — universal box breakdown calculator
 *
 * rem        — remainingQuantity in NATIVE UNIT (m², meters, or pcs — as stored in DB)
 * product    — product doc with: itemsPerBox, sizePerItem, category.measurementUnit
 *
 * Returns null if product has no itemsPerBox (not a boxed product)
 * Otherwise returns:
 *   totalPcs   — rem converted to pieces
 *   boxes      — full boxes
 *   extraPcs   — leftover pieces after full boxes
 *   sizeLabel  — "11.16 m²" or "10.2 m" or "31 pcs"
 *   boxLabel   — "7 boxes + 3 pcs" or "3 pcs"
 *   unitLabel  — "m²" | "m" | "pcs"
 */
export function getBoxInfo(rem, product) {
  if (!product?.itemsPerBox) return null;

  const unit        = product?.category?.measurementUnit || "pcs";
  const sizePerItem = product?.sizePerItem;
  const itemsPerBox = product?.itemsPerBox;

  const round = (n, p = 10) =>
    Math.round(n * Math.pow(10, p)) / Math.pow(10, p);

  // Convert native unit → pieces with float-safe rounding
  const totalPcs = (unit === "pcs" || !sizePerItem)
    ? Math.round(rem)
    : Math.round(round(rem / sizePerItem)); // ✅ round before Math.round

  const boxes    = Math.floor(totalPcs / itemsPerBox);
  const extraPcs = totalPcs % itemsPerBox;

  const unitLabel = unit === "m2"
    ? "m²"
    : unit === "linear_meter"
    ? "m"
    : "pcs";

  const sizeLabel = unit === "pcs"
    ? `${rem} pcs`
    : `${round(+rem, 3)} ${unitLabel}`;

  const boxLabel = boxes > 0
    ? `${boxes} box${boxes !== 1 ? 'es' : ''}${extraPcs > 0 ? ` + ${extraPcs} pcs` : ''}`
    : `${extraPcs} pcs`;

  return { totalPcs, boxes, extraPcs, sizeLabel, boxLabel, unitLabel };
}